import React from 'react';
import { toast } from 'react-toastify';
import type { WorkbookInstance } from '@fortune-sheet/react';
import { locale, type Context } from '@fortune-sheet/core';
import { DATE_FORMATS, formatDateByFormat } from '../../../utils/dateUtils';
import { serialToMs } from '../converters';
import type { FortuneCell } from '../types';
import {
    updateCell,
    isEditingInContext,
    commitEditThen,
    handleSum,
    createFilter,
    clearFilter,
    moveHighlightCell,
    toolbarItemClickHandler,
    insertRowCol,
    deleteRowCol,
    hideSelected,
    showSelected,
    handleLink,
    getNowDateTime,
    handleFormulaInput,
    execFunctionGroup,
    clearFormulaAt,
    recomputeDisplayValue,
    isPartiallyMerged,
    getInternals,
    type WorkbookInternals,
} from './workbookInternals';
import { applyNumberFormatToSelection } from './useFormatCellsDialog';
import { RUPEE_SYMBOL, isDateCell, hasTimeFormatToken, stripFormatLiterals } from './numberFormats';
import { shiftFormulaRefs, parseGoToRef, type GoToResult } from './formulaRefs';
import { pickStyle, carryMc } from './pasteSpecial';
import { fullRowColRange, nextSheetId, firstUnfrozenCell, lastUsedCell, fillDownExtent, liveDims, type SelectionRange } from './navigation';

/** A single row or column axis — shared by every insert/delete/hide action
 *  below instead of each repeating the `'row' | 'column'` union. */
export type Axis = 'row' | 'column';

// Mirror core's own insertRowCol thresholds exactly — Excel's sheet size since
// aica-patch:excel-limits (`data.length + count > 1,048,576` for rows,
// `data[0].length + count > 16,384` for columns; the library's 10,000 / 1,000
// refused a row on any 30k-row statement) — named so insertAxis's guard reads
// as the library's own limit, not a bare magic number.
const ROW_INSERT_LIMIT = 1_048_576;
const COLUMN_INSERT_LIMIT = 16_384;

// Shared by insertAxis/deleteAxis's recipe backstop — an unanticipated throw
// (e.g. readOnly) the pre-checks above didn't catch.
const INSERT_DELETE_BACKSTOP_FAILURE_MESSAGE = 'Could not complete that action. Please try again.';

/**
 * What Ctrl+S commits. Order: commit any in-progress edit (mirrors
 * the library's own "commit before X" recipe, e.g. `react:7269-7278`'s
 * add-sheet handler) → one animation frame (so the commit's own state update
 * has landed) → THEN evaluate `hasPendingChanges()`, so a just-committed edit
 * counts → `handleSave()` or the "All changes saved" no-op toast.
 *
 * `!canSave` (a view context) returns before `preventDefault`, so the
 * browser's own Ctrl+S runs as it does on main; `historyOpen` short-circuits
 * before any of that — Ctrl+S never reaches `handleSave` in either case.
 */
export interface SaveShortcutArgs {
    e: { preventDefault: () => void; repeat?: boolean };
    canSave: boolean;
    historyOpen: boolean;
    internals: WorkbookInternals | null;
    handleSave: () => void;
    hasPendingChanges: () => boolean;
}

export const saveShortcut = ({ e, canSave, historyOpen, internals, handleSave, hasPendingChanges }: SaveShortcutArgs): void => {
    if (!canSave) return;
    e.preventDefault();
    if (e.repeat) return;
    if (historyOpen) {
        toast.info('Close version history to save.');
        return;
    }

    commitEditThen(internals, () => {
        if (hasPendingChanges()) {
            handleSave();
        } else {
            toast.info('All changes saved');
        }
    });
};

/** Builds a `<div><p>message</p><button>label</button></div>` toast body.
 *  Plain `React.createElement` (not JSX) because this file is `.ts`, not `.tsx`. */
const buildActionToastContent = (message: string, action?: { label: string; onClick: () => void }): React.ReactElement =>
    React.createElement(
        'div',
        null,
        React.createElement('p', null, message),
        action ? React.createElement('button', { type: 'button', onClick: action.onClick }, action.label) : null,
    );

/** The grid has no print layout, so Ctrl/Cmd+P is
 *  intercepted rather than left to print a blank/garbled page. */
export const printGuard = (e: { preventDefault: () => void }, handleDownloadXlsx: () => void): void => {
    e.preventDefault();
    toast(
        buildActionToastContent("Printing isn't supported here. Download as .xlsx to print.", {
            label: 'Download',
            onClick: handleDownloadXlsx,
        }),
    );
};

/** The active cell (row_focus/column_focus of the LAST selected range) —
 *  prefers `getContext().luckysheet_select_save` (patch), falls back to the
 *  public `getSelection()` (loses row_focus/column_focus precision, so it
 *  uses the range's first cell instead). Shared by navHome and the
 *  Name Box. */
export const activeCellRowCol = (wb: WorkbookInstance | null, internals: WorkbookInternals | null): [number, number] => {
    if (internals) {
        const ctx = internals.getContext();
        const last = ctx.luckysheet_select_save?.[ctx.luckysheet_select_save.length - 1];
        if (last) return [last.row_focus ?? last.row[0], last.column_focus ?? last.column[0]];
    }
    const selection = wb?.getSelection?.();
    const last = selection?.[selection.length - 1];
    return [last?.row[0] ?? 0, last?.column[0] ?? 0];
};

/**
 * Ctrl+Enter: commits any in-progress edit, then fills that same
 * value into every OTHER cell of the full multi-range selection — a formula
 * is ref-shifted per target cell (mirrors a copy/paste-down), a literal value
 * is copied verbatim. One `setContextWithProduce`, so one undo step. The
 * selection itself is left exactly as it was (Excel keeps the multi-range
 * selection after Ctrl+Enter).
 *
 * Every filled cell carries the active cell's own type/format (Excel
 * behaviour): a literal fill copies ONLY v, a deep-copied ct, and a
 * recomputed m onto the target — routing `String(activeCell.v)` through
 * `updateCell` re-infers a bare "General" number from that string, losing a
 * date/percent/currency/text-typed cell's type and format (a date filled as
 * 45667, 12% as 0.12); a raw `{...activeCell}` overwrite instead drops the
 * target's OWN styling (bl/bg/fc/…) and copies the source's mc (merge)/ps
 * (comment)/hl (hyperlink), none of which belong on a fill target. Rebuilt
 * from `pickStyle(dest)` (the target's OWN style only) rather than mutating
 * the existing object in place, so a formula target's own `f` never
 * survives into the new object — left in place, it would re-evaluate on the
 * next recalc/reopen and silently overwrite the literal value just filled;
 * `clearFormulaAt` mirrors `updateCell`'s own full cleanup for a formula
 * cell overwritten by a literal — both the cell's calcChain entry AND its
 * formulaCache.formulaCellInfoMap entry, or a later edit to the formula's
 * OLD dependency can resurrect it via that separate, rarely-invalidated
 * cache. A formula fill still needs `updateCell` to evaluate the shifted
 * formula's
 * OWN (per-target) value and register it into calcChain, so only its `ct`
 * is overridden afterward to match the active cell's format, with `m`
 * recomputed for that cell's own evaluated value. `execFunctionGroup` is
 * the same dependent-recalc trigger `updateCell`'s own commit calls
 * internally — reachable standalone for the literal-fill path's direct
 * write (mirrors usePasteSpecial.ts's applyPastePlan, which solved the
 * identical write-directly-but-still-recalc-dependents problem).
 */
export const fillSelection = (internals: WorkbookInternals): void => {
    const ctx = internals.getContext();
    const ranges = ctx.luckysheet_select_save ?? [];
    const partiallyMerged = ranges.some((range) => isPartiallyMerged(ctx, range.row[0], range.row[1], range.column[0], range.column[1]));
    if (partiallyMerged) {
        toast.error(locale(ctx).generalDialog.partiallyError);
        return;
    }

    internals.setContextWithProduce((d) => {
        const refs = internals.getRefs();
        const editing = isEditingInContext(d);
        const lastSelected = d.luckysheet_select_save?.[d.luckysheet_select_save.length - 1];
        const [activeR, activeC] = editing
            ? [d.luckysheetCellUpdate[0], d.luckysheetCellUpdate[1]]
            : [lastSelected?.row_focus ?? lastSelected?.row[0] ?? 0, lastSelected?.column_focus ?? lastSelected?.column[0] ?? 0];
        if (editing) updateCell?.(d, activeR, activeC, refs.cellInput);

        const sheet = d.luckysheetfile.find((s) => s.id === d.currentSheetId);
        const activeCell = sheet?.data?.[activeR]?.[activeC];
        if (!sheet?.data || !activeCell) return;

        (d.luckysheet_select_save ?? []).forEach((range) => {
            for (let r = range.row[0]; r <= range.row[1]; r += 1) {
                for (let c = range.column[0]; c <= range.column[1]; c += 1) {
                    if (r === activeR && c === activeC) continue;
                    if (!sheet.data![r]) sheet.data![r] = [];
                    if (activeCell.f) {
                        const shifted = shiftFormulaRefs(activeCell.f, r - activeR, c - activeC);
                        updateCell?.(d, r, c, undefined, shifted);
                        const target = sheet.data![r][c];
                        if (target && activeCell.ct) {
                            target.ct = { ...activeCell.ct };
                            const m = recomputeDisplayValue(activeCell.ct.fa ?? 'General', target.v);
                            if (m !== undefined) target.m = m;
                        }
                    } else {
                        // Rebuilds the target from its own style only
                        // (pickStyle — bl/bg/fc/…, never v/f/mc/ps/hl) — a
                        // formula target's own `f` must NOT survive a literal
                        // fill, or it re-evaluates and overwrites this value
                        // on the next recalc/reopen. clearFormulaAt mirrors
                        // updateCell's own full "formula cell overwritten by
                        // a literal" cleanup so the cell also stops being
                        // tracked in calcChain AND formulaCellInfoMap.
                        const dest = sheet.data![r][c];
                        if (dest?.f) clearFormulaAt(d, r, c, d.currentSheetId);
                        // The guard above already refused any selection that
                        // partially overlaps a merge, so a target carrying
                        // `mc` here is always part of a FULLY-selected merge
                        // (see carryMc's own doc comment) — keep it, or the
                        // fill would silently unmerge it.
                        const target = {
                            ...pickStyle(dest),
                            v: activeCell.v,
                            ct: activeCell.ct ? { ...activeCell.ct } : undefined,
                            ...carryMc(dest),
                        };
                        const m = recomputeDisplayValue(activeCell.ct?.fa ?? 'General', activeCell.v);
                        if (m !== undefined) target.m = m;
                        sheet.data![r][c] = target;
                        execFunctionGroup?.(d, r, c, activeCell.v);
                    }
                }
            }
        });
    });
};

/** Ctrl+Space / Shift+Space: expands the current selection's last
 *  range to full column(s) or row(s) (`fullRowColRange`) and re-selects it. */
export const selectRowsOrCols = (wb: WorkbookInstance, mode: 'row' | 'col'): void => {
    const selection = wb.getSelection?.();
    const last = selection?.[selection.length - 1];
    if (!last) return;
    const sheet = wb.getSheet();
    const dims = liveDims(sheet);
    const range = fullRowColRange({ row: last.row as [number, number], column: last.column as [number, number] }, dims, mode);
    wb.setSelection([range]);
};

/** Ctrl+PgUp/PgDn, Mac ⌥←/→, and the Alt+PgUp/PgDn fallback: activates the
 *  next/previous VISIBLE sheet in tab order — a no-op past either end (no
 *  wrap). Alt+↑/↓ and Ctrl+Shift+PgUp/PgDn are no longer switchSheet. */
export const switchSheet = (wb: WorkbookInstance, dir: 'next' | 'prev'): void => {
    const targetId = nextSheetId(wb.getAllSheets(), wb.getSheet()?.id, dir);
    if (targetId) wb.activateSheet({ id: targetId });
};

/** Scrolls so the cell sits at the top-left of the scrolling area (one axis alone when the other is
 *  omitted). Fortune's targetRow/targetColumn scroll puts the cell at the sheet's top-left, which on
 *  a frozen-pane sheet is under the panes; without the live offsets that scroll is used as is. */
const scrollToCell = (wb: WorkbookInstance, { r, c }: { r?: number; c?: number }): void => {
    const ctx = getInternals(wb)?.getContext();
    if (!ctx?.visibledatarow?.length || !ctx.visibledatacolumn?.length) {
        wb.scroll({ targetRow: r, targetColumn: c });
        return;
    }
    const pane = firstUnfrozenCell(wb.getSheet()?.frozen); // the rows and columns before it are frozen
    const paneHeight = pane.r > 0 ? ctx.visibledatarow[pane.r - 1] : 0;
    const paneWidth = pane.c > 0 ? ctx.visibledatacolumn[pane.c - 1] : 0;
    const options: { scrollTop?: number; scrollLeft?: number } = {};
    if (r != null) options.scrollTop = Math.max(0, (r > 0 ? ctx.visibledatarow[r - 1] : 0) - paneHeight);
    if (c != null) options.scrollLeft = Math.max(0, (c > 0 ? ctx.visibledatacolumn[c - 1] : 0) - paneWidth);
    wb.scroll(options);
};

/** Home: same row, jump to the first unfrozen column. */
export const navHome = (wb: WorkbookInstance, internals: WorkbookInternals | null): void => {
    const [row] = activeCellRowCol(wb, internals);
    const firstCol = firstUnfrozenCell(wb.getSheet()?.frozen).c;
    wb.setSelection([{ row: [row, row], column: [firstCol, firstCol] }]);
    scrollToCell(wb, { c: firstCol });
};

/** Ctrl/Cmd+Home: the first unfrozen cell. */
export const navCtrlHome = (wb: WorkbookInstance): void => {
    const { r, c } = firstUnfrozenCell(wb.getSheet()?.frozen);
    wb.setSelection([{ row: [r, r], column: [c, c] }]);
    scrollToCell(wb, { r, c });
};

/** Ctrl/Cmd+End: the sheet's last used cell. */
export const navCtrlEnd = (wb: WorkbookInstance): void => {
    const sheet = wb.getSheet();
    const { r, c } = lastUsedCell(sheet?.celldata ?? []);
    wb.setSelection([{ row: [r, r], column: [c, c] }]);
    scrollToCell(wb, { r, c });
};

/** Shift+Home / Ctrl+Shift+Home / Ctrl+Shift+End: extends the selection to a
 *  rectangle spanning the active cell and `target` (Home/CtrlHome/CtrlEnd's
 *  own destination) — the anchor is whichever of the two corners isn't the
 *  active cell. */
const extendTo = (wb: WorkbookInstance, internals: WorkbookInternals | null, target: { r: number; c: number }): void => {
    const [activeR, activeC] = activeCellRowCol(wb, internals);
    wb.setSelection([
        {
            row: [Math.min(activeR, target.r), Math.max(activeR, target.r)],
            column: [Math.min(activeC, target.c), Math.max(activeC, target.c)],
        },
    ]);
};

/** Shift+Home: extends to the first unfrozen column, same row. */
export const extendHome = (wb: WorkbookInstance, internals: WorkbookInternals | null): void => {
    const [row] = activeCellRowCol(wb, internals);
    const firstCol = firstUnfrozenCell(wb.getSheet()?.frozen).c;
    extendTo(wb, internals, { r: row, c: firstCol });
};

/** Ctrl+Shift+Home: extends to the first unfrozen cell. */
export const extendCtrlHome = (wb: WorkbookInstance, internals: WorkbookInternals | null): void => {
    extendTo(wb, internals, firstUnfrozenCell(wb.getSheet()?.frozen));
};

/** Ctrl+Shift+End: extends to the sheet's last used cell. */
export const extendCtrlEnd = (wb: WorkbookInstance, internals: WorkbookInternals | null): void => {
    const sheet = wb.getSheet();
    extendTo(wb, internals, lastUsedCell(sheet?.celldata ?? []));
};

/** Enter / Shift+Enter, not editing: moves the active cell down/up WITHIN
 *  the current selection (Excel), instead of the library's own
 *  `handleGlobalEnter` (which always starts an edit). */
export const enterMove = (internals: WorkbookInternals, dir: 'down' | 'up'): void => {
    internals.setContextWithProduce((d) => {
        moveHighlightCell?.(d, 'down', dir === 'down' ? 1 : -1, 'rangeOfSelect');
    });
};

/** Tab / Shift+Tab (editing, no autocomplete dropdown) and Shift+Enter
 *  (editing): commits the in-progress edit, then moves right/left/up. */
export const commitAndMove = (internals: WorkbookInternals, dir: 'left' | 'right' | 'up'): void => {
    internals.setContextWithProduce((d) => {
        const refs = internals.getRefs();
        updateCell?.(d, d.luckysheetCellUpdate[0], d.luckysheetCellUpdate[1], refs.cellInput);
        if (dir === 'up') moveHighlightCell?.(d, 'down', -1, 'rangeOfSelect');
        else moveHighlightCell?.(d, 'right', dir === 'right' ? 1 : -1, 'rangeOfSelect');
    });
};

/** Ctrl/Cmd+I / Ctrl/Cmd+U — the same path the toolbar's Bold/Italic/
 *  Underline buttons use (`toolbarItemClickHandler`, core export `:80760`),
 *  reached directly since Fortune swallows these two shortcuts itself. */
export const toggleFontStyle = (internals: WorkbookInternals, style: 'italic' | 'underline'): void => {
    internals.setContextWithProduce((d) => {
        const refs = internals.getRefs();
        toolbarItemClickHandler?.(style)?.(d, refs.cellInput);
    });
};

/** Ctrl/Cmd+Shift+L: toggles the active sheet's filter — mirrors the
 *  toolbar filter button exactly (create when absent, clear when present).
 *  `filter`/`filter_select` are content-op path roots (a filter is part of
 *  what a save carries), so this arms autosave as the toolbar filter does. */
export const toggleFilter = (internals: WorkbookInternals): void => {
    internals.setContextWithProduce((d) => {
        const sheet = d.luckysheetfile.find((s) => s.id === d.currentSheetId);
        if (sheet?.filter_select) clearFilter?.(d);
        else createFilter?.(d);
    });
};

/** Alt+=: the library's own AutoSum — it already looks up (falling
 *  back to left when nothing is above) for the range to sum. */
export const autoSum = (internals: WorkbookInternals): void => {
    internals.setContextWithProduce((d) => {
        const refs = internals.getRefs();
        handleSum?.(d, refs.cellInput, refs.fxInput, refs.globalCache);
    });
};

export type RowColSelectionKind = Axis | 'partial';

/** Classifies the first selected range against the sheet's live extent: a
 *  full-row selection spans every column, a full-column selection spans
 *  every row — anything else is `'partial'` (Ctrl+-/Ctrl+Shift+= open
 *  the delete/insert dialog for those instead of guessing). `null` with no
 *  selection at all. */
export const classifySelection = (wb: WorkbookInstance): RowColSelectionKind | null => {
    const sel = wb.getSelection?.()?.[0];
    if (!sel) return null;
    const dims = liveDims(wb.getSheet());
    const fullRow = sel.column[0] === 0 && sel.column[1] >= dims.cols - 1;
    const fullColumn = sel.row[0] === 0 && sel.row[1] >= dims.rows - 1;
    if (fullRow && !fullColumn) return 'row';
    if (fullColumn && !fullRow) return 'column';
    return 'partial';
};

/** The shared deleteAxis/insertAxis prologue: no selection at all is a
 *  silent no-op, and a multi-range selection mirrors the library's own
 *  noMulti refusal — insert/delete against more than one selected range is
 *  ambiguous (which range's span applies?). Returns the resolved context and
 *  the single selected range, or `null` once it has already shown whichever
 *  toast (if any) applies. */
const resolveSingleAxisSelection = (
    wb: WorkbookInstance,
    internals: WorkbookInternals,
): { ctx: Context; sel: { row: number[]; column: number[] } } | null => {
    const selection = wb.getSelection?.();
    if (!selection?.length) return null;
    const ctx = internals.getContext();
    if (selection.length > 1) {
        toast.error(locale(ctx).rightclick.noMulti);
        return null;
    }
    // Right after a tab switch, before any click, Fortune's selection is [start, null]: one cell, as the grid shows it.
    // A null end made the count NaN, and insertRowCol then shifted the tab's calcChain to row NaN, which broke the
    // formula map rebuild and stopped recalculation in every later tab.
    const span = (pair: (number | null | undefined)[]): number[] | null =>
        pair[0] == null ? null : [pair[0], pair[1] == null ? pair[0] : pair[1]];
    const row = span(selection[0].row);
    const column = span(selection[0].column);
    if (!row || !column) return null;
    return { ctx, sel: { row, column } };
};

/** Deletes exactly `axis`'s span of the current selection — the full-row/
 *  column fast path (`deleteCells`) and the delete/insert dialog's explicit
 *  "Entire row(s)"/"Entire column(s)" choice for a partial one share this.
 *  Fortune's own deleteRowCol already re-indexes config.rowhidden/colhidden
 *  itself (core: drops a key inside the deleted range, shifts one after it
 *  down by the deleted span) — verified directly against the library source
 *  and empirically against a real mounted Workbook; no extra handling needed
 *  here.
 *
 *  Ports the library's own cannotDeleteAllRow/Column guard (react
 *  :7748-7807) — checked OUTSIDE the state updater so the toast never runs
 *  inside a React state updater — plus a try/catch backstop inside the
 *  recipe for any other throw (e.g. readOnly), mirroring the library's own
 *  delete menu. */
export const deleteAxis = (wb: WorkbookInstance, internals: WorkbookInternals, axis: Axis): void => {
    const resolved = resolveSingleAxisSelection(wb, internals);
    if (!resolved) return;
    const { ctx, sel } = resolved;
    const op =
        axis === 'row'
            ? { type: 'row' as const, start: sel.row[0], end: sel.row[1] }
            : { type: 'column' as const, start: sel.column[0], end: sel.column[1] };
    const slen = (axis === 'row' ? sel.row[1] - sel.row[0] : sel.column[1] - sel.column[0]) + 1;

    const sheet = ctx.luckysheetfile.find((s) => s.id === ctx.currentSheetId);
    const extent = axis === 'row' ? sheet?.data?.length ?? 0 : sheet?.data?.[0]?.length ?? 0;
    if (extent <= slen) {
        toast.error(locale(ctx).rightclick[axis === 'row' ? 'cannotDeleteAllRow' : 'cannotDeleteAllColumn']);
        return;
    }

    internals.setContextWithProduce(
        (d) => {
            try {
                deleteRowCol?.(d, op);
            } catch (err) {
                // Backstop for a throw the extent check above didn't
                // anticipate (e.g. readOnly) — mirrors react:7793/7751.
                console.error('[sheetActions] deleteRowCol failed', err);
                toast.error(INSERT_DELETE_BACKSTOP_FAILURE_MESSAGE);
            }
        },
        { deleteRowColOp: { ...op, id: ctx.currentSheetId } },
    );
};

/** Inserts `axis`'s span of the current selection above/left of it — shared
 *  by `insertCells`'s full-row/column fast path and the delete/insert dialog's
 *  explicit choice. Fortune's own insertRowCol already re-indexes
 *  config.rowhidden/colhidden itself (same verification as deleteAxis above);
 *  no extra handling needed here.
 *
 *  Ports the library's own maxExceeded guard (ROW_INSERT_LIMIT/
 *  COLUMN_INSERT_LIMIT, top of file) — checked OUTSIDE the state updater,
 *  same reasoning as deleteAxis — plus a try/catch backstop inside the
 *  recipe, mirroring the library's own insert menu (react:7639-7646/7693-7700). */
export const insertAxis = (wb: WorkbookInstance, internals: WorkbookInternals, axis: Axis): void => {
    const resolved = resolveSingleAxisSelection(wb, internals);
    if (!resolved) return;
    const { ctx, sel } = resolved;
    const count = (axis === 'row' ? sel.row[1] - sel.row[0] : sel.column[1] - sel.column[0]) + 1;
    const op =
        axis === 'row'
            ? { type: 'row' as const, index: sel.row[0], count, direction: 'lefttop' as const }
            : { type: 'column' as const, index: sel.column[0], count, direction: 'lefttop' as const };

    const sheet = ctx.luckysheetfile.find((s) => s.id === ctx.currentSheetId);
    const overLimit =
        axis === 'row'
            ? (sheet?.data?.length ?? 0) + count > ROW_INSERT_LIMIT
            : (sheet?.data?.[0]?.length ?? 0) + count > COLUMN_INSERT_LIMIT;
    if (overLimit) {
        toast.error(locale(ctx).rightclick[axis === 'row' ? 'rowOverLimit' : 'columnOverLimit']);
        return;
    }

    internals.setContextWithProduce(
        (d) => {
            try {
                insertRowCol?.(d, op);
            } catch (err) {
                // Backstop for a throw the limit check above didn't
                // anticipate (e.g. readOnly) — mirrors react:7643/7697.
                console.error('[sheetActions] insertRowCol failed', err);
                toast.error(INSERT_DELETE_BACKSTOP_FAILURE_MESSAGE);
            }
        },
        { insertRowColOp: { ...op, id: ctx.currentSheetId } },
    );
};

/** The delete/insert dialog's sensible default radio choice for the CURRENT
 *  selection — looser than classifySelection's mutually-exclusive 'row'/
 *  'column'/'partial' (a full-sheet Ctrl+A selection spans both axes and
 *  classifies as 'partial' there, the only shape the dialog ever opens for):
 *  defaults to 'column' only when the selection spans every ROW
 *  (column-like), 'row' for everything else, including a true interior block. */
export const defaultDeleteInsertAxis = (wb: WorkbookInstance): Axis => {
    const sel = wb.getSelection?.()?.[0];
    if (!sel) return 'row';
    const dims = liveDims(wb.getSheet());
    const fullColumn = sel.row[0] === 0 && sel.row[1] >= dims.rows - 1;
    return fullColumn ? 'column' : 'row';
};

/** Ctrl+- (Cmd+-): a full-row/column selection deletes those rows/columns
 *  outright; a partial selection defers to the delete/insert dialog (`onOpenDialog`)
 *  rather than guessing which axis the user meant. */
export const deleteCells = (wb: WorkbookInstance, internals: WorkbookInternals, onOpenDialog: () => void): void => {
    const kind = classifySelection(wb);
    if (!kind) return;
    if (kind === 'partial') {
        onOpenDialog();
        return;
    }
    deleteAxis(wb, internals, kind);
};

/** Ctrl+Shift+=: the insert counterpart of `deleteCells` — rows above /
 *  columns left of the (full-row/column) selection; a partial selection
 *  likewise opens the delete/insert dialog. */
export const insertCells = (wb: WorkbookInstance, internals: WorkbookInternals, onOpenDialog: () => void): void => {
    const kind = classifySelection(wb);
    if (!kind) return;
    if (kind === 'partial') {
        onOpenDialog();
        return;
    }
    insertAxis(wb, internals, kind);
};

/** Ctrl+9/Ctrl+0 (hide) and Ctrl+Shift+9/Ctrl+Shift+0 (unhide) — the
 *  library's own row/column visibility toggle for the current selection. */
export const hideOrShow = (internals: WorkbookInternals, type: Axis, show: boolean): void => {
    internals.setContextWithProduce((d) => {
        if (show) showSelected?.(d, type);
        else hideSelected?.(d, type);
    });
};

/** Parses Go To / Name Box input against the ACTIVE sheet's
 *  current dims — shared so both callers build `parseGoToRef`'s `dims`
 *  argument the same way (the active sheet's row/col count) rather than
 *  each re-deriving it from `wb.getSheet()`. */
export const resolveGoToRef = (wb: WorkbookInstance, text: string): GoToResult => {
    const sheet = wb.getSheet();
    return parseGoToRef(text, wb.getAllSheets(), liveDims(sheet));
};

/** Go To / Name Box navigation — switches sheet first (when
 *  the ref was sheet-qualified), then selects and scrolls to `range`. Shared
 *  by GoToDialog and NameBox so both land on the target the same way. */
export const goToRange = (wb: WorkbookInstance, range: SelectionRange, sheetId?: string): void => {
    if (sheetId && sheetId !== wb.getSheet()?.id) wb.activateSheet({ id: sheetId });
    wb.setSelection([range]);
    scrollToCell(wb, { r: range.row[0], c: range.column[0] });
};

/** Fill-handle double-click: fills DOWN from `sel`'s whole block (every row
 *  and column it spans, not just its top-left cell) to the end of the
 *  nearest non-empty neighbour column's contiguous run (`fillDownExtent`) —
 *  a no-op when neither neighbour has data. */
export const fillDownDblClick = (wb: WorkbookInstance, sel: SelectionRange): void => {
    const sheet = wb.getSheet();
    const extent = fillDownExtent(sheet?.celldata ?? [], sel);
    if (!extent) return;
    wb.autoFillCell(sel, extent, 'down');
};

/** The library's own InputBox has a `useLayoutEffect` reacting to
 *  `luckysheetCellUpdate` changing that reseeds the cell input with the
 *  CELL'S OWN raw value (react:940-985), unless this flag is set — the
 *  library's own date/time-insert handler sets it for exactly this reason
 *  (core:77026, `cache.ignoreWriteCell = true`) before writing its own text.
 *  Without it, our text is overwritten a moment after we set it (this
 *  silently made insertDate/insertTime a no-op — the cell's stale value
 *  reappeared instead). */
interface GlobalCacheWithIgnoreWriteCell {
    ignoreWriteCell?: boolean;
}

/** Starts editing the active cell with `text` already typed in — mirrors
 *  Excel's own "insert as if typed" behaviour for Ctrl+;/Ctrl+Shift+;/
 *  Ctrl+Shift+' (the library's own path instead stamped a fixed literal
 *  timestamp string), and for the first printable character of a
 *  "type to replace" edit (useSheetFocus.ts's focus-lost fallback). */
export const startEditWithText = (internals: WorkbookInternals, r: number, c: number, text: string): void => {
    internals.setContextWithProduce((d) => {
        d.luckysheetCellUpdate = [r, c];
        const refs = internals.getRefs();
        (refs.globalCache as GlobalCacheWithIgnoreWriteCell).ignoreWriteCell = true;
        if (refs.cellInput) {
            refs.cellInput.innerText = text;
            // Syncs the formula bar to match — the same call the library's
            // own date/time handler makes right after its own cellInput
            // write (core:77033).
            handleFormulaInput?.(d, refs.fxInput, refs.cellInput, 0);
        }
    });
};

/** Ctrl+; (Cmd+; is insertTime, not this): today's date, `yyyy-MM-dd`. */
export const insertDate = (wb: WorkbookInstance, internals: WorkbookInternals): void => {
    const [r, c] = activeCellRowCol(wb, internals);
    startEditWithText(internals, r, c, getNowDateTime?.(1) ?? '');
};

/** Ctrl+Shift+; / Ctrl+Shift+: / Mac Cmd+;: the current time only, `HH:mm`
 *  (the library's own Ctrl+; inserted a full date+time stamp instead). */
export const insertTime = (wb: WorkbookInstance, internals: WorkbookInternals): void => {
    const [r, c] = activeCellRowCol(wb, internals);
    startEditWithText(internals, r, c, formatDateByFormat(Date.now(), DATE_FORMATS.TIME_24H));
};

/** The text Ctrl+Shift+' copies from the cell above. A number seeds its raw
 *  `v` (a percent cell seeds `v*100` with a `%` — the same way a user would
 *  type it): the displayed `m` is never re-parsed, because core's `genarate`
 *  keeps only the part before the decimal point of a "1,234.56" and rejects
 *  other display shapes. A date falls back to ISO (Fortune doesn't recognise
 *  every display format, e.g. `d-mmm-yy`, when re-typed), keeping the
 *  time-of-day for a date+time format. A pure TIME format keeps the displayed
 *  `m`, which `genarate` reads back as the same time. Text and booleans use `m`. */
const copyAboveText = (cell: FortuneCell | null | undefined): string => {
    const fa = cell?.ct?.fa ?? '';
    if (isDateCell(cell) && typeof cell?.v === 'number') {
        const fmt = hasTimeFormatToken(fa) ? DATE_FORMATS.ISO_DATE_TIME : DATE_FORMATS.ISO;
        // allowZero: a date cell can legitimately serialize to epoch 0
        // (Excel serial 25569, 1970-01-01) — formatDateByFormat's default
        // treats 0 as absent, which every other (non-spreadsheet) caller
        // relies on.
        return formatDateByFormat(serialToMs(cell.v), fmt, { tz: 'UTC', allowZero: true });
    }
    if (typeof cell?.v === 'number' && !hasTimeFormatToken(fa)) {
        // toPrecision(15) keeps 0.07 * 100 from seeding 7.000000000000001.
        return stripFormatLiterals(fa).includes('%') ? `${Number((cell.v * 100).toPrecision(15))}%` : String(cell.v);
    }
    if (cell?.m != null) return String(cell.m);
    if (cell?.v != null) return String(cell.v);
    return '';
};

/** Ctrl+Shift+': the value from the cell directly above (Excel) — a no-op
 *  on row 0, where there is no cell above. */
export const copyAbove = (wb: WorkbookInstance, internals: WorkbookInternals): void => {
    const [r, c] = activeCellRowCol(wb, internals);
    if (r === 0) return;
    const above = wb.getSheet()?.data?.[r - 1]?.[c];
    startEditWithText(internals, r, c, copyAboveText(above));
};

/** Ctrl/Cmd+K: opens the insert/edit-link card for the current selection —
 *  the same path the toolbar's Link button uses. */
export const insertLink = (internals: WorkbookInternals): void => {
    internals.setContextWithProduce((d) => {
        handleLink?.(d);
    });
};

// $ % # ! @ ^ ~, matched on e.code (Digit4/5/3/1/2/6/Backquote).
const NUM_FMT_PRESET_BY_CODE: Readonly<Record<string, string>> = Object.freeze({
    Digit4: `"${RUPEE_SYMBOL}"#,##0.00`, // $ currency
    Digit5: '0%', // % percentage
    Digit3: 'd-mmm-yy', // # date
    Digit1: '#,##0.00', // ! number
    Digit2: 'h:mm AM/PM', // @ time
    Digit6: '0.00E+00', // ^ scientific
    Backquote: 'General', // ~ general
});

/** Ctrl+Shift+$ % # ! @ ^ ~: applies the matching number-format preset to
 *  the current selection via the SAME path Format Cells' own Apply uses. */
export const numFmtPreset = (wb: WorkbookInstance, internals: WorkbookInternals, code: string): void => {
    const fa = NUM_FMT_PRESET_BY_CODE[code];
    const selection = wb.getSelection?.();
    if (!fa || !selection?.length) return;
    applyNumberFormatToSelection(
        wb,
        internals,
        selection.map((s) => ({ row: [s.row[0], s.row[1]], column: [s.column[0], s.column[1]] })),
        fa,
    );
};

// Fortune's own dropdown trigger for a data-validation list cell — a fixed,
// single DOM id (react:3879).
const DATA_VALIDATION_DROPDOWN_ID = 'luckysheet-dataVerification-dropdown-btn';
// One .luckysheet-filter-options button per filtered column, rendered in
// column order — there is no per-button column-index attribute to query by.
const FILTER_BUTTON_SELECTOR = '.luckysheet-filter-options';

/** Alt+↓: on a cell with a data-validation dropdown, opens it. On a filter
 *  header cell, opens that column's filter menu. Otherwise a no-op (the
 *  caller leaves the key unintercepted). Returns whether it acted. */
export const openCellDropdown = (wb: WorkbookInstance, internals: WorkbookInternals | null): boolean => {
    const validationBtn = document.getElementById(DATA_VALIDATION_DROPDOWN_ID);
    if (validationBtn && validationBtn.offsetParent !== null) {
        validationBtn.click();
        return true;
    }

    const ctx = internals?.getContext();
    const sheet = ctx?.luckysheetfile.find((s) => s.id === ctx.currentSheetId);
    const filterRange = sheet?.filter_select;
    if (!filterRange) return false;
    const [activeR, activeC] = activeCellRowCol(wb, internals);
    if (activeR !== filterRange.row[0] || activeC < filterRange.column[0] || activeC > filterRange.column[1]) return false;

    const buttons = document.querySelectorAll<HTMLElement>(FILTER_BUTTON_SELECTOR);
    const btn = buttons[activeC - filterRange.column[0]];
    if (!btn) return false;
    btn.click();
    return true;
};
